import { useState, useEffect, useRef } from "react";
import { BookAppointmentModal } from "components/BookAppointment";
import DoctorCarousel from "components/DoctorCarousel";
import scrollToSection from "utils/scrollToSection";
import mapsUrl from "utils/mapsUrl";
import visibleStats from "utils/visibleStats";
import AddressesModal from "components/site/AddressesModal";
import buildImgTransform from "utils/imgTransform";
import { SpotlightCard, useParallax, Masonry, ChromaVeil, chromaTrack } from "components/site/fx";
import { StarBorder } from "components/site/textfx";
import SplashCursor from "components/site/SplashCursor";
import { DecryptedText } from "components/site/textanim";
import GalleryCarousel from "components/site/GalleryCarousel";
import HeroReadMore from "components/HeroReadMore";

// ── Default placeholder images (replaced by admin uploads) ───────────────────
const PLACEHOLDER_HERO = "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1600&q=80";
const PLACEHOLDER_GALLERY = [
  "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&q=80",
  "https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=800&q=80",
  "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=800&q=80",
  "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80",
];

// ── WhatsApp helper ───────────────────────────────────────────────────────────
const buildWhatsAppHref = (cfg) => {
  if (!cfg?.whatsapp_enabled || !cfg?.contact_phone) return null;
  const digits = String(cfg.contact_phone).replace(/\D/g, '');
  if (!digits) return null;
  const base = `https://wa.me/${digits}`;
  return cfg.whatsapp_message
    ? `${base}?text=${encodeURIComponent(cfg.whatsapp_message)}`
    : base;
};

// ── Star rating ───────────────────────────────────────────────────────────────
const Stars = ({ rating, color }) => (
  <span style={{ color: color || '#f59e0b', fontSize: '0.85rem', letterSpacing: 1 }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} style={{ opacity: i < (rating || 5) ? 1 : 0.3 }}>★</span>
    ))}
  </span>
);

// ── WhatsApp icon ─────────────────────────────────────────────────────────────
const WhatsAppIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// ── Animated counter ──────────────────────────────────────────────────────────
function Counter({ target }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        obs.disconnect();
        let start = 0;
        const end = parseInt(target, 10) || 0;
        if (end === 0) { setVal(0); return; }
        const step = Math.ceil(end / 60);
        const timer = setInterval(() => {
          start += step;
          if (start >= end) { setVal(end); clearInterval(timer); }
          else setVal(start);
        }, 20);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{val.toLocaleString()}</span>;
}

// ── Image Carousel ────────────────────────────────────────────────────────────
function ImageCarousel({ images, accent, title, dark, T }) {
  if (!images || images.length === 0) return null;
  return (
    <section style={{ padding: '80px 24px', background: T.bgSection }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div data-reveal style={{ textAlign: 'center', marginBottom: 48 }}>
          <p style={{ color: accent, fontWeight: 600, fontSize: '0.82rem', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>
            Our Clinic
          </p>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 700, color: T.text, fontFamily: "'Argent CF', Georgia, serif", marginBottom: 0 }}>
            {title || 'A Look Inside'}
          </h2>
        </div>
        <GalleryCarousel images={images} accent={accent} />
      </div>
    </section>
  );
}

// ── Safe Image (onError fallback) ────────────────────────────────────────────
const SafeImg = ({ src, fallback, alt = '', style = {} }) => (
  <img src={src} alt={alt} style={style}
    onError={e => { if (e.target.src !== fallback) e.target.src = fallback; }} />
);

// ── Why Choose Us Carousel ────────────────────────────────────────────────────
function WhyCarousel({ items, images, accent, T, onBook, id }) {
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const fallback = PLACEHOLDER_GALLERY[0];

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const t = setInterval(() => setSlide(i => (i + 1) % items.length), 5500);
    return () => clearInterval(t);
  }, [items.length, paused]);

  if (!items.length) return null;
  const photoBg = images && images.length > 0
    ? images[slide % images.length]
    : PLACEHOLDER_GALLERY[slide % PLACEHOLDER_GALLERY.length];

  return (
    <section id={id} style={{ background: T.bg, padding: '80px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 44 }}>
          <p style={{ color: accent, fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>Why Us</p>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 700, color: T.text, fontFamily: "'Argent CF', Georgia, serif" }}>Why Patients Choose Us</h2>
        </div>
        <div className="ti-why-card" style={{ borderRadius: 24, overflow: 'hidden', boxShadow: '0 28px 72px rgba(0,0,0,0.18)', display: 'flex', minHeight: 400 }}
          onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          {/* Left — photo */}
          <div className="ti-why-photo" style={{ flex: '0 0 45%', position: 'relative', overflow: 'hidden' }}>
            {(items.length > 0 ? items : [null]).map((_, i) => (
              <SafeImg key={i} src={images && images.length > 0 ? images[i % images.length] : PLACEHOLDER_GALLERY[i % PLACEHOLDER_GALLERY.length]} fallback={fallback} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: i === slide ? 1 : 0, transition: 'opacity 0.65s ease' }} />
            ))}
            <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${accent}88 0%, rgba(0,0,0,0.5) 100%)` }} />
            <div style={{ position: 'absolute', bottom: 28, left: 28 }}>
              <div style={{ fontSize: '5rem', fontWeight: 900, lineHeight: 1, color: 'rgba(255,255,255,0.18)', fontFamily: "'Argent CF', Georgia, serif", userSelect: 'none' }}>{String(slide + 1).padStart(2, '0')}</div>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 4 }}>of {items.length} reasons</div>
            </div>
          </div>
          {/* Right — content */}
          <div style={{ flex: 1, background: T.bgCard, padding: 'clamp(32px,5vw,56px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: `${accent}14`, borderRadius: 50, padding: '5px 14px', marginBottom: 20, alignSelf: 'flex-start' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: accent, display: 'inline-block' }} />
              <span style={{ fontSize: '0.73rem', fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Reason {String(slide + 1).padStart(2, '0')}</span>
            </div>
            <p style={{ fontSize: 'clamp(16px, 1.8vw, 22px)', color: T.text, lineHeight: 1.68, fontFamily: "'Argent CF', Georgia, serif", marginBottom: 36, flex: 1 }}>{items[slide]}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <button aria-label="Previous" onClick={() => setSlide(i => (i - 1 + items.length) % items.length)} style={{ width: 42, height: 42, borderRadius: '50%', border: `1.5px solid ${accent}`, background: 'transparent', color: accent, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.2s, color 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = accent; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = accent; }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg></button>
              <div style={{ display: 'flex', gap: 6 }}>
                {items.map((_, i) => (
                  <button key={i} aria-label={`Reason ${i + 1}`} onClick={() => setSlide(i)} style={{ width: i === slide ? 22 : 8, height: 8, borderRadius: 4, background: i === slide ? accent : `${accent}35`, border: 'none', cursor: 'pointer', transition: 'all 0.3s', padding: 0 }} />
                ))}
              </div>
              <button aria-label="Next" onClick={() => setSlide(i => (i + 1) % items.length)} style={{ width: 42, height: 42, borderRadius: '50%', border: `1.5px solid ${accent}`, background: 'transparent', color: accent, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.2s, color 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = accent; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = accent; }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg></button>
              <button onClick={onBook} style={{ marginLeft: 'auto', background: accent, color: '#fff', border: 'none', borderRadius: 50, padding: '10px 22px', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>Book Appointment →</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Photo Collage ─────────────────────────────────────────────────────────────
function PhotoCollage({ images, accent, stats, adjust = {} }) {
  const photos = images && images.length > 0 ? images : PLACEHOLDER_GALLERY;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {[0, 1, 2, 3].map((i) => {
        const src = photos[i];
        if (src) {
          return (
            <div key={i} style={{ borderRadius: 14, overflow: 'hidden', aspectRatio: '1/1', position: 'relative' }}>
              <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: buildImgTransform(adjust[src]), transformOrigin: 'center' }}
                onError={e => { const fb = PLACEHOLDER_GALLERY[i % PLACEHOLDER_GALLERY.length]; if (e.target.src !== fb) e.target.src = fb; }} />
              {i === 0 && (parseInt(stats?.bookings, 10) || 0) > 0 && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '16px 14px' }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                    {parseInt(stats.bookings, 10).toLocaleString()}+
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4 }}>
                    Appointments Served
                  </div>
                </div>
              )}
            </div>
          );
        }
        // Fallback color boxes
        const bk = parseInt(stats?.bookings, 10) || 0;
        const dr = parseInt(stats?.doctors, 10) || 0;
        const boxes = [
          bk > 0 ? { bg: accent, color: '#fff', stat: `${bk.toLocaleString()}+`, label: 'Appointments' } : { bg: accent, color: '#fff', icon: '🩺', text: 'Trusted, personal care' },
          { bg: '#1f2937', color: '#fff', icon: '🏥', text: 'Comprehensive care' },
          { bg: '#f3f4f6', color: '#111', icon: '👨‍⚕️', text: 'Expert doctors' },
          dr > 0 ? { bg: `${accent}18`, color: accent, stat: `${dr}+`, label: 'Expert Doctors' } : { bg: `${accent}18`, color: accent, icon: '⭐', text: 'Highly rated' },
        ];
        const b = boxes[i];
        return (
          <div key={i} style={{ background: b.bg, borderRadius: 14, padding: '20px 16px', aspectRatio: '1/1', display: 'flex', flexDirection: 'column', justifyContent: b.stat ? 'flex-end' : 'center', color: b.color }}>
            {b.stat ? (
              <>
                <div style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1 }}>{b.stat}</div>
                <div style={{ fontSize: '0.72rem', opacity: 0.8, marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{b.label}</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '1.4rem', marginBottom: 8 }}>{b.icon}</div>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.5 }}>{b.text}</div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Condition enrichment — auto-tags from keywords ──────────────────────────
const COND_LIBRARY = [
  { kw: ['skin','eczema','psoriasis','acne','allerg','rash','dermat'], badge: 'Dermatology', period: '4–12 weeks', ages: 'All ages', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
  { kw: ['thyroid','diabetes','hormone','endocrin'], badge: 'Endocrine', period: 'Ongoing care', ages: 'Adults 20+', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
  { kw: ['asthma','sinusit','respirat','lung','breath','allerg'], badge: 'Respiratory', period: '6–8 weeks', ages: 'All ages', icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z' },
  { kw: ['digest','gastric','stomach','ibs','bowel','liver','constip'], badge: 'Gastroenterology', period: '2–6 weeks', ages: 'All ages', icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z' },
  { kw: ['hair','scalp','alopecia'], badge: 'Trichology', period: '8–16 weeks', ages: 'Ages 15+', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  { kw: ["women","female","gynec","menstr","pcod","pcos"], badge: 'Women\'s Health', period: 'Ongoing', ages: 'Women 12–55', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
  { kw: ['child','pediatr','infant','baby'], badge: 'Paediatrics', period: 'As needed', ages: 'Under 14', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
  { kw: ['stress','anxiety','sleep','mental','depress','insomnia'], badge: 'Mind & Body', period: '4–10 weeks', ages: 'Adults 18+', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
  { kw: ['joint','arthrit','rheumat','bone','ortho','back','pain','spine'], badge: 'Musculoskeletal', period: '6–12 weeks', ages: 'Adults 30+', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
];
function enrichCondition(str) {
  const s = str.toLowerCase();
  for (const lib of COND_LIBRARY) {
    if (lib.kw.some(k => s.includes(k))) return lib;
  }
  return { badge: 'General Care', period: 'Varies', ages: 'All ages', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' };
}

// ─────────────────────────────────────────────────────────────────────────────
export default function TemplateImpact({ config, companyData }) {
  const [heroRef, heroParallax] = useParallax(0.18);
  const [modalOpen, setModalOpen] = useState(false);
  const [addrOpen, setAddrOpen] = useState(false);
  const [preselect, setPreselect] = useState({ doctorId: null, specId: null });
  const openBookWith = (doctorId, specId) => { setPreselect({ doctorId, specId }); setModalOpen(true); };
  const [dark, setDark] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredWhy, setHoveredWhy] = useState(null);
  const [hoveredCond, setHoveredCond] = useState(null);
  const [bioDoc, setBioDoc] = useState(null); // doctor card showing full bio (hover/tap)

  const acc = config.accentColor || '#16a34a';

  const T = dark
    ? { bg: '#0a0a0f', bgCard: '#141420', bgSection: '#0f0f1a', text: '#f0f0f8', textMid: '#b0b0c8', textMuted: '#6868a0', border: 'rgba(255,255,255,0.07)', navBg: 'rgba(10,10,15,0.97)' }
    : { bg: '#ffffff', bgCard: '#f8f8fc', bgSection: '#f2f2f8', text: '#1a1a2e', textMid: '#374151', textMuted: '#6b7280', border: 'rgba(0,0,0,0.08)', navBg: 'rgba(255,255,255,0.97)' };

  const whatsappHref = buildWhatsAppHref(config);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll('[data-reveal]');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.style.opacity = '1'; e.target.style.transform = 'translateY(0) scale(1)'; e.target.style.filter = 'blur(0)'; }
      }),
      { threshold: 0.1 }
    );
    els.forEach((el) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(32px) scale(0.98)';
      el.style.filter = 'blur(5px)';
      el.style.transition = 'opacity 0.7s ease, transform 0.7s cubic-bezier(.2,.7,.2,1), filter 0.7s ease';
      obs.observe(el);
    });
    return () => obs.disconnect();
  }, [dark]);

  const smoothScroll = (id) => {
    scrollToSection(id);
    setMobileMenuOpen(false);
  };

  const maxW = { maxWidth: 1200, margin: '0 auto' };
  const sectionPad = { padding: '80px 24px' };

  const features = config.features || [];
  const testimonials = (config.testimonials || []).slice(0, 8);
  const conditions = config.conditions || [];
  const whyChooseUs = config.whyChooseUs || [];
  const locations = config.locations || [];
  const doctorsList = (config.doctors || []).filter((d) => d.is_active && d.show_on_website !== false);
  const doctorsSection = config.doctorsSection || { enabled: true, title: 'Meet Our Doctors' };
  const stats = config.stats || {};
  const statLabels = config.statLabels || {};
  const heroStats = visibleStats(config, { bookings: 'Appointments', doctors: 'Expert Doctors', users: 'Happy Patients' });

  const heroBg = config.heroBannerImage || PLACEHOLDER_HERO;
  const galleryImages = (config.galleryImages || []).filter(Boolean);
  const carouselImages = (config.galleryCarousel || []).filter(Boolean);
  const imgAdjust = config.imageAdjust || {};

  // Menu order: About → Services (features) → Reviews → Contact.
  const navLinks = [
    ['about', 'About'],
    ...(features.length > 0 ? [['services', 'Services']] : []),
    ...(testimonials.length > 0 ? [['testimonials', 'Reviews']] : []),
    ['contact', 'Contact'],
  ];

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: T.bg, color: T.text, minHeight: '100vh', overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        h1, h2, h3, p, span { overflow-wrap: break-word; }
        html { scroll-behavior: smooth; }
        section[id], footer[id] { scroll-margin-top: 80px; }
        @keyframes floatBlob { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-20px) scale(1.04)} }
        @media (max-width: 768px) {
          .ti-hero-inner { flex-direction: column !important; text-align: center !important; }
          .ti-why-card { flex-direction: column !important; min-height: 0 !important; }
          .ti-why-photo { flex: 0 0 220px !important; }
          .ti-hero-stats { gap: 0 !important; align-items: center !important; }
          .ti-hero-stats > div { flex: 1 1 0 !important; justify-content: center !important; min-width: 0 !important; }
          .ti-stat-divider { margin: 0 8px !important; height: 34px !important; }
          .ti-pill-cloud { flex-direction: column !important; align-items: stretch !important; }
          .ti-pill { width: 100% !important; min-height: 62px !important; }
          .ti-nav-cta { display: none !important; }
          .ti-grid-4 { grid-template-columns: repeat(2,1fr) !important; }
          .ti-grid-3 { grid-template-columns: 1fr !important; }
          .ti-grid-2 { grid-template-columns: 1fr !important; }
          .ti-about-layout { flex-direction: column !important; }
          .ti-footer-cols { grid-template-columns: 1fr 1fr !important; }
          .ti-footer-info { flex-direction: column !important; }
          .ti-nav-desktop { display: none !important; }
          .ti-mob-btn { display: flex !important; }
          .ti-hide-mobile { display: none !important; }
        }
        @media (min-width: 769px) { .ti-mob-btn { display: none !important; } .ti-mob-menu { display: none !important; } }
      `}</style>

      {/* ── STICKY NAV ── */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, background: T.navBg, backdropFilter: 'blur(12px)', borderBottom: navScrolled ? `1px solid ${T.border}` : '1px solid transparent', boxShadow: navScrolled ? '0 4px 20px rgba(0,0,0,0.1)' : 'none', transition: 'box-shadow 0.3s, border-color 0.3s', padding: '0 24px' }}>
        <div style={{ ...maxW, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
          {/* Brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: acc, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
              {config.logo
                ? <img src={config.logo} alt={config.companyName} style={{ width: '100%', height: '100%', objectFit: 'contain', transform: config.logoTransform }} />
                : <span style={{ color: '#fff', fontWeight: 800, fontSize: '1rem' }}>{config.companyName?.charAt(0) || 'C'}</span>}
            </div>
            <span style={{ fontWeight: 700, fontSize: '1rem', color: T.text }}>{config.companyName}</span>
          </div>

          {/* Desktop links */}
          <div className="ti-nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {navLinks.map(([id, label]) => (
              <button key={id} onClick={() => smoothScroll(id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500, color: T.textMid, padding: '7px 15px', borderRadius: 999, transition: 'color 0.25s, background 0.25s, transform 0.25s' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = acc; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = T.textMid; e.currentTarget.style.background = 'none'; e.currentTarget.style.transform = 'none'; }}>
                {label}
              </button>
            ))}
          </div>

          {/* Right actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {whatsappHref && (
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="ti-hide-mobile"
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#25d366', color: '#fff', border: 'none', borderRadius: 50, padding: '7px 14px', fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none', transition: 'opacity 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <WhatsAppIcon size={15} />
                WhatsApp
              </a>
            )}
            <button className="ti-nav-cta" onClick={() => setDark(!dark)} style={{ width: 34, height: 34, borderRadius: '50%', border: `1px solid ${T.border}`, background: T.bgCard, color: T.text, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem' }}>
              {dark ? '☀️' : '🌙'}
            </button>
            <button className="ti-nav-cta" onClick={() => setModalOpen(true)} style={{ background: acc, color: '#fff', border: 'none', borderRadius: 50, padding: '8px 18px', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', transition: 'opacity 0.2s', whiteSpace: 'nowrap' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
              Book Appointment
            </button>
            {/* Mobile menu toggle */}
            <button className="ti-mob-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ display: 'none', background: 'none', border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 8px', cursor: 'pointer', flexDirection: 'column', gap: 4 }}>
              <span style={{ display: 'block', width: 18, height: 2, background: T.text }} />
              <span style={{ display: 'block', width: 18, height: 2, background: T.text }} />
              <span style={{ display: 'block', width: 18, height: 2, background: T.text }} />
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="ti-mob-menu" style={{ display: 'flex', flexDirection: 'column', padding: '16px 20px 22px', gap: 4, borderTop: `1px solid ${T.border}`, background: dark ? 'rgba(10,10,15,0.78)' : 'rgba(255,255,255,0.78)', backdropFilter: 'blur(22px) saturate(180%)', WebkitBackdropFilter: 'blur(22px) saturate(180%)', borderBottomLeftRadius: 18, borderBottomRightRadius: 18, boxShadow: '0 18px 40px rgba(0,0,0,0.18)' }}>
            {navLinks.map(([id, label]) => (
              <button key={id} onClick={() => smoothScroll(id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.text, textAlign: 'left', padding: '10px 0', borderBottom: `1px solid ${T.border}`, fontSize: '0.95rem' }}>
                {label}
              </button>
            ))}
            {whatsappHref && (
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#25d366', color: '#fff', borderRadius: 8, padding: '10px 16px', marginTop: 12, textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem' }}>
                <WhatsAppIcon size={16} /> Chat on WhatsApp
              </a>
            )}
            <button onClick={() => { setModalOpen(true); setMobileMenuOpen(false); }} style={{ marginTop: 10, background: acc, color: '#fff', border: 'none', borderRadius: 50, padding: '12px', fontWeight: 700, cursor: 'pointer' }}>
              Book Appointment
            </button>
          </div>
        )}
      </nav>

      {/* ── HERO — Full banner image with overlay ── */}
      <section style={{ minHeight: '90vh', position: 'relative', display: 'flex', alignItems: 'center', padding: '120px 24px 80px', overflow: 'hidden' }}>
        {/* Background image */}
        <div ref={heroRef} style={{ position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'brightness(0.38)', overflow: 'hidden' }}>
          <img src={heroBg} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', transform: `${heroParallax} scale(1.18)`, transition: 'transform 0.1s linear' }} onError={e => { e.target.src = PLACEHOLDER_HERO; }} />
        </div>
        {/* Gradient overlay */}
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${acc}cc 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.5) 100%)` }} />

        <div style={{ ...maxW, width: '100%', position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 50, padding: '6px 16px', marginBottom: 24 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#86efac', display: 'inline-block' }} />
            <span style={{ color: '#fff', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Trusted Healthcare</span>
          </div>

          <h1 style={{ fontFamily: "'Argent CF', Georgia, serif", fontSize: 'clamp(36px, 6vw, 68px)', fontWeight: 900, color: '#fff', lineHeight: 1.12, marginBottom: 16, letterSpacing: '-0.02em' }}>
            <DecryptedText text={config.companyName || ''} animateOn="view" sequential speed={45} revealDirection="start" />
          </h1>

          {config.tagline && (
            <h2 style={{ fontSize: 'clamp(16px, 2.5vw, 26px)', fontWeight: 500, color: 'rgba(255,255,255,0.92)', marginBottom: 16, letterSpacing: '-0.01em' }}>
              {config.tagline}
            </h2>
          )}

          {config.heroSubtext && (
            <p style={{ fontSize: 'clamp(14px, 1.5vw, 17px)', color: 'rgba(255,255,255,0.78)', maxWidth: 600, margin: '0 auto 12px', lineHeight: 1.7 }}>
              {config.heroSubtext}
            </p>
          )}
          <HeroReadMore paragraphs={config.heroExtraParagraphs} heading={config.companyName} linkColor="#fff" />


          <div style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap', marginTop: 36, marginBottom: 52 }}>
            <StarBorder color="#ffffff">
              <button onClick={() => setModalOpen(true)} style={{ background: 'transparent', color: '#fff', border: 'none', padding: '13px 30px', borderRadius: 50, fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}>
                Book Appointment
              </button>
            </StarBorder>
            {whatsappHref && (
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#25d366', color: '#fff', border: 'none', padding: '13px 28px', borderRadius: 50, fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', textDecoration: 'none', transition: 'opacity 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <WhatsAppIcon size={18} /> WhatsApp
              </a>
            )}
            <button onClick={() => smoothScroll('about')} style={{ background: 'transparent', color: '#fff', border: '2px solid rgba(255,255,255,0.7)', padding: '11px 26px', borderRadius: 50, fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', transition: 'background 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              Learn More
            </button>
          </div>

          {/* Stats row */}
          {heroStats.length > 0 && (
          <div className="ti-hero-stats" style={{ display: 'flex', justifyContent: 'center', gap: 0, flexWrap: 'wrap' }}>
            {heroStats.map((s, i) => (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center' }}>
                {i > 0 && <div className="ti-stat-divider" style={{ width: 1, height: 44, background: 'rgba(255,255,255,0.25)', margin: '0 24px' }} />}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 'clamp(26px, 4vw, 42px)', fontWeight: 800, color: '#fff', fontFamily: "'Argent CF', Georgia, serif", lineHeight: 1 }}>
                    <Counter target={s.val} /><span>+</span>
                  </div>
                  <div style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.7)', marginTop: 5, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* ── WHY CHOOSE US — image+content carousel ── */}
      {whyChooseUs.length > 0 && (
        <WhyCarousel items={whyChooseUs} images={galleryImages.length > 0 ? galleryImages : PLACEHOLDER_GALLERY} accent={acc} T={T} onBook={() => setModalOpen(true)} />
      )}

      {/* ── FEATURES — dark glassmorphism 2×3 grid ── */}
      {features.length > 0 && (
        <section id="services" style={{ ...sectionPad, background: dark ? '#080c14' : '#0a0e1a' }}>
          <div style={maxW}>
            <div data-reveal style={{ textAlign: 'center', marginBottom: 52 }}>
              <p style={{ color: acc, fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>Services</p>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 700, color: '#fff', fontFamily: "'Argent CF', Georgia, serif" }}>What We Offer</h2>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'center' }}>
              {features.map((f, i) => (
                <SpotlightCard key={i} glow={acc} radius={20} borderColor="rgba(255,255,255,0.1)"
                  style={{ flex: '1 1 260px', maxWidth: 320, background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(12px)' }}>
                  <div data-reveal style={{ padding: '36px 28px', textAlign: 'center' }}>
                    <div style={{ width: 68, height: 68, borderRadius: 18, background: `${acc}22`, border: `1px solid ${acc}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '1.9rem' }}>
                      {f.icon && typeof f.icon === 'string' && f.icon.length <= 4 ? f.icon : '🩺'}
                    </div>
                    <h3 style={{ fontWeight: 700, fontSize: '1.08rem', color: '#fff', marginBottom: 10 }}>{f.title}</h3>
                    <p style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.68, fontSize: '0.91rem' }}>{f.desc}</p>
                  </div>
                </SpotlightCard>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CONDITIONS — dark pill-cloud layout ── */}
      {conditions.length > 0 && (
        <section style={{ ...sectionPad, background: dark ? '#080c14' : '#0a0e1a' }}>
          <div style={maxW}>
            <div data-reveal style={{ textAlign: 'center', marginBottom: 52 }}>
              <p style={{ color: acc, fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>Specialties</p>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 700, color: '#fff', fontFamily: "'Argent CF', Georgia, serif", marginBottom: 10 }}>Conditions We Treat</h2>
              {config.conditionsTagline && <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1rem', maxWidth: 500, margin: '0 auto', lineHeight: 1.65 }}>{config.conditionsTagline}</p>}
            </div>
            {/* Pill cloud */}
            <div data-reveal className="ti-pill-cloud" style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center' }}>
              {conditions.map((c, i) => {
                const meta = enrichCondition(c);
                const hov = hoveredCond === i;
                return (
                  <div key={i} className="ti-pill"
                    onMouseEnter={() => setHoveredCond(i)}
                    onMouseLeave={() => setHoveredCond(null)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: hov ? acc : 'rgba(255,255,255,0.07)',
                      border: `1px solid ${hov ? acc : 'rgba(255,255,255,0.13)'}`,
                      borderRadius: 50,
                      padding: '10px 20px 10px 12px',
                      cursor: 'default',
                      transition: 'all 0.22s ease',
                      transform: hov ? 'translateY(-3px) scale(1.04)' : 'none',
                      boxShadow: hov ? `0 8px 28px ${acc}40` : 'none',
                    }}>
                    {/* Icon circle */}
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: hov ? 'rgba(255,255,255,0.18)' : `${acc}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={hov ? '#fff' : acc} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={meta.icon}/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: hov ? '#fff' : 'rgba(255,255,255,0.88)', lineHeight: 1.2 }}>{c}</div>
                      <div style={{ fontSize: '0.68rem', color: hov ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                        {meta.badge}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── ABOUT — Photo collage left, text right ── */}
      <section id="about" style={{ ...sectionPad, background: T.bgSection }}>
        <div style={maxW}>
          <div className="ti-about-layout" style={{ display: 'flex', gap: 48, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Left: masonry gallery */}
            <div style={{ flex: '0 0 42%', minWidth: 280 }} data-reveal>
              <Masonry images={(galleryImages.length > 0 ? galleryImages : PLACEHOLDER_GALLERY).slice(0, 5)} columns={2} gap={10} radius={16} accent={acc} />
            </div>
            {/* Right: text */}
            <div style={{ flex: '1 1 50%', minWidth: 280 }} data-reveal>
              <p style={{ color: acc, fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>About Us</p>
              <h2 style={{ fontFamily: "'Argent CF', Georgia, serif", fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 700, color: T.text, marginBottom: 20, lineHeight: 1.25 }}>
                {config.companyName}
              </h2>
              {config.aboutWhoWeAre && (
                <p style={{ color: T.textMid, lineHeight: 1.75, fontSize: '0.97rem', marginBottom: 14 }}>{config.aboutWhoWeAre}</p>
              )}
              {config.aboutWhoWeAreBullets && config.aboutWhoWeAreBullets.length > 0 && (
                <ul style={{ listStyle: 'none', marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {config.aboutWhoWeAreBullets.map((b, i) => (
                    <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: T.textMid, fontSize: '0.95rem', lineHeight: 1.6 }}>
                      <span style={{ color: acc, fontWeight: 700, flexShrink: 0 }}>✓</span>{b}
                    </li>
                  ))}
                </ul>
              )}
              {config.aboutMission && (
                <div style={{ borderLeft: `4px solid ${acc}`, background: `${acc}08`, borderRadius: '0 12px 12px 0', padding: '14px 18px', marginTop: 16 }}>
                  <p style={{ color: T.textMid, fontStyle: 'italic', lineHeight: 1.7, fontSize: '0.93rem' }}>{config.aboutMission}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR APPROACH — timeline ── */}
      {config.aboutApproach && (
        <section style={{ ...sectionPad, background: dark ? '#070b0f' : '#0f2419' }}>
          <div style={maxW}>
            <div data-reveal style={{ textAlign: 'center', marginBottom: 56 }}>
              <p style={{ color: acc, fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>Methodology</p>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 700, color: '#fff', fontFamily: "'Argent CF', Georgia, serif" }}>Our Approach</h2>
            </div>
            {(() => {
              const steps = Array.isArray(config.aboutApproach) ? config.aboutApproach : config.aboutApproach.split(/\.\s+/).filter(Boolean);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0, maxWidth: 760, margin: '0 auto' }}>
                  {steps.map((step, i) => (
                    <div key={i} data-reveal style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
                      {/* Timeline spine */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: acc, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1rem', boxShadow: `0 6px 20px ${acc}60`, zIndex: 1, position: 'relative' }}>
                          {String(i + 1).padStart(2, '0')}
                        </div>
                        {i < steps.length - 1 && <div style={{ width: 2, height: 40, background: `linear-gradient(to bottom, ${acc}60, transparent)`, marginTop: 4 }} />}
                      </div>
                      {/* Card */}
                      <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: '22px 24px', marginBottom: 16, flex: 1, transition: 'background 0.2s, border-color 0.2s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = `${acc}50`; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}>
                        <p style={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.72, fontSize: '0.95rem', margin: 0 }}>{step}{!step.endsWith('.') ? '.' : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {/* ── GALLERY CAROUSEL ── */}
      {carouselImages.length > 0 && (
        <ImageCarousel images={carouselImages} accent={acc} title={config.galleryTitle} dark={dark} T={T} />
      )}

      {/* ── MEET OUR DOCTORS ── */}
      {doctorsSection.enabled && doctorsList.length > 0 && (
        <section onMouseMove={chromaTrack} style={{ padding: 'clamp(60px,9vw,110px) 24px', background: T.bg, position: 'relative' }}>
          <div style={{ ...maxW }}>
            <div style={{ textAlign: 'center', marginBottom: 50 }} data-reveal>
              <span style={{ display: 'inline-block', background: `${acc}1f`, color: acc, borderRadius: 100, padding: '4px 14px', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Our Team</span>
              <h2 style={{ fontSize: 'clamp(26px, 3.4vw, 42px)', fontWeight: 800, color: T.text, letterSpacing: '-0.02em' }}>{doctorsSection.title || 'Meet Our Doctors'}</h2>
            </div>
            <DoctorCarousel accent={acc}>
              {doctorsList.map((d, i) => (
                <div key={d.id} style={{ position: 'relative', background: T.bgCard, borderRadius: 20, overflow: 'hidden', border: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', transition: 'transform 0.22s, box-shadow 0.22s' }}
                  >
                  <div style={{ aspectRatio: '4 / 3', background: `linear-gradient(135deg, ${acc}26, ${acc}0a)`, position: 'relative', overflow: 'hidden' }}>
                    {d.photo ? (
                      <img src={d.photo} alt={d.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transform: buildImgTransform({ scale: d.photo_scale, position: d.photo_position }), transformOrigin: 'center' }} />
                    ) : (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4rem', fontWeight: 800, color: acc, opacity: 0.4 }}>
                        {d.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '22px 22px 24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <h3 style={{ fontSize: '1.08rem', fontWeight: 700, color: T.text, marginBottom: 4 }}>{d.name}</h3>
                    {d.qualifications && <div style={{ fontSize: '0.78rem', color: T.textMuted, marginBottom: 8 }}>{d.qualifications}</div>}
                    {d.specialization_name && (
                      <div style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: '0.7rem', fontWeight: 700, color: acc, background: `${acc}1f`, padding: '3px 10px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                        {d.specialization_name}
                      </div>
                    )}
                    {d.experience_years > 0 && (
                      <div style={{ fontSize: '0.85rem', color: T.textMid, marginBottom: 8 }}>
                        <span style={{ color: acc, fontWeight: 700 }}>{d.experience_years}+</span> {d.experience_years === 1 ? 'year' : 'years'} of experience
                      </div>
                    )}
                    {d.bio && <p style={{ fontSize: '0.85rem', color: T.textMid, lineHeight: 1.65, marginBottom: 6, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{d.bio}</p>}
                    {d.bio && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setBioDoc((cur) => (cur === d.id ? null : d.id)); }}
                        style={{ alignSelf: 'flex-end', background: 'none', border: 'none', color: acc, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', padding: 0, marginBottom: 18 }}>
                        View full bio →
                      </button>
                    )}
                    <button onClick={() => openBookWith(d.id, d.specialization)}
                      style={{ marginTop: 'auto', background: acc, color: '#fff', border: 'none', borderRadius: 50, padding: '11px 18px', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer' }}>
                      Book Appointment →
                    </button>
                  </div>

                  {/* Full-bio reveal — covers the card on hover/tap, reverts on mouse-leave */}
                  {d.bio && (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 4, borderRadius: 20, background: T.bgCard, padding: 22, display: 'flex', flexDirection: 'column', gap: 8, opacity: bioDoc === d.id ? 1 : 0, pointerEvents: bioDoc === d.id ? 'auto' : 'none', transition: 'opacity 0.25s', overflowY: 'auto' }}>
                      <button type="button" onClick={() => setBioDoc(null)} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: acc, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', padding: 0, marginBottom: 2 }}>← Back</button>
                      <h3 style={{ fontSize: '1.08rem', fontWeight: 700, color: T.text, margin: 0 }}>{d.name}</h3>
                      {d.specialization_name && <div style={{ fontSize: '0.7rem', fontWeight: 700, color: acc, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{d.specialization_name}</div>}
                      <p style={{ fontSize: '0.84rem', color: T.textMid, lineHeight: 1.65, margin: '4px 0 0', whiteSpace: 'pre-line' }}>{d.bio}</p>
                      <button onClick={() => openBookWith(d.id, d.specialization)}
                        style={{ marginTop: 'auto', background: acc, color: '#fff', border: 'none', borderRadius: 50, padding: '11px 18px', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer' }}>
                        Book Appointment →
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </DoctorCarousel>
          </div>
          <ChromaVeil />
        </section>
      )}

      {/* ── TESTIMONIALS — masonry-style featured layout ── */}
      {testimonials.length > 0 && (
        <section id="testimonials" style={{ ...sectionPad, background: dark ? T.bgSection : `${acc}07` }}>
          <div style={maxW}>
            <div data-reveal style={{ textAlign: 'center', marginBottom: 52 }}>
              <p style={{ color: acc, fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>Reviews</p>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 700, color: T.text, fontFamily: "'Argent CF', Georgia, serif" }}>What Our Patients Say</h2>
            </div>
            {/* Featured first review + grid of the rest */}
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {/* Featured large card */}
              {testimonials[0] && (
                <div data-reveal style={{ flex: '0 0 clamp(280px, 38%, 420px)', background: acc, borderRadius: 22, padding: '36px 32px', color: '#fff', boxShadow: `0 20px 56px ${acc}30` }}>
                  <div style={{ fontSize: '3rem', lineHeight: 1, marginBottom: 16, fontFamily: "'Argent CF', Georgia, serif", opacity: 0.5 }}>"</div>
                  <p style={{ fontSize: '1.05rem', lineHeight: 1.72, marginBottom: 24, color: 'rgba(255,255,255,0.92)', fontStyle: 'italic' }}>{testimonials[0].feedback}</p>
                  <div style={{ display: 'flex', gap: 2, marginBottom: 14 }}>
                    {[1,2,3,4,5].map(s => <span key={s} style={{ color: s <= (testimonials[0].rating || 5) ? '#fde68a' : 'rgba(255,255,255,0.3)', fontSize: '1rem' }}>★</span>)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem' }}>
                      {(testimonials[0].user__first_name?.charAt(0) || 'P').toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{[testimonials[0].user__first_name, testimonials[0].user__last_name].filter(Boolean).join(' ') || 'Patient'}</div>
                      <div style={{ fontSize: '0.74rem', opacity: 0.72 }}>Verified Patient</div>
                    </div>
                  </div>
                </div>
              )}
              {/* Remaining reviews grid */}
              <div style={{ flex: 1, minWidth: 280, display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 16, alignContent: 'start' }}>
                {testimonials.slice(1, 7).map((t, i) => (
                  <div key={t.id || i} data-reveal style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 16, padding: '22px 20px', transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = `${acc}40`; e.currentTarget.style.boxShadow = `0 8px 28px ${acc}14`; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                    <div style={{ fontSize: '1.4rem', color: acc, lineHeight: 1, marginBottom: 8, fontFamily: "'Argent CF', Georgia, serif", opacity: 0.6 }}>"</div>
                    <p style={{ color: T.textMid, fontSize: '0.88rem', lineHeight: 1.62, marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.feedback}</p>
                    <div style={{ display: 'flex', gap: 1, marginBottom: 8 }}>
                      {[1,2,3,4,5].map(s => <span key={s} style={{ color: s <= (t.rating || 5) ? '#f59e0b' : '#d1d5db', fontSize: '0.8rem' }}>★</span>)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: acc, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.82rem', flexShrink: 0 }}>
                        {(t.user__first_name?.charAt(0) || 'P').toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: T.text }}>{[t.user__first_name, t.user__last_name].filter(Boolean).join(' ') || 'Patient'}</div>
                        <div style={{ fontSize: '0.7rem', color: T.textMuted }}>Verified Patient</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── CLOSING CTA ── */}
      <section style={{ background: `linear-gradient(135deg, ${acc} 0%, ${acc}cc 100%)`, padding: '64px 24px', textAlign: 'center' }}>
        <div style={maxW} data-reveal>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 42px)', fontWeight: 700, color: '#fff', fontFamily: "'Argent CF', Georgia, serif", marginBottom: 14, lineHeight: 1.2 }}>
            {config.closingCta || 'Start Your Healing Journey'}
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.82)', marginBottom: 32, fontSize: '1.05rem', maxWidth: 480, margin: '0 auto 32px' }}>
            Take the first step toward better health today.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap' }}>
            <button onClick={() => setModalOpen(true)} style={{ background: '#fff', color: acc, border: 'none', borderRadius: 50, padding: '14px 36px', fontSize: '1rem', fontWeight: 800, cursor: 'pointer', boxShadow: '0 8px 28px rgba(0,0,0,0.15)', transition: 'transform 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
              Book Appointment
            </button>
            {whatsappHref && (
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#25d366', color: '#fff', borderRadius: 50, padding: '14px 32px', fontWeight: 700, fontSize: '1rem', textDecoration: 'none' }}>
                <WhatsAppIcon size={18} /> Chat on WhatsApp
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer id="contact" style={{ position: 'relative', overflow: 'hidden' }}>
        <SplashCursor colors={[acc, '#ffffff', '#22d3ee']} />
        <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Info cards strip */}
        <div style={{ background: dark ? '#0d0d18' : '#1a2332', padding: '40px 24px' }}>
          <div className="ti-footer-info" style={{ ...maxW, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {[
              { icon: '📞', title: 'Contact', value: [config.contact_phone, config.contact_email].filter(Boolean).join(' · ') || 'Book via appointment form' },
              ...(locations.length > 0 ? [{
                icon: '📍',
                title: locations.length === 1 ? 'Location' : `Locations (${locations.length})`,
                value: (
                  <div style={{ maxHeight: locations.length > 3 ? 220 : 'none', overflowY: locations.length > 3 ? 'auto' : 'visible', paddingRight: locations.length > 3 ? 6 : 0 }}>
                    {locations.map((l, idx) => {
                      const isObj = typeof l === 'object' && l !== null;
                      const nm = isObj ? (l.name || `Branch ${idx + 1}`) : l;
                      const addr = isObj ? l.address : '';
                      return (
                        <a key={isObj && l.id ? l.id : idx} href={mapsUrl(l)} target="_blank" rel="noopener noreferrer" title={addr || nm}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: idx < locations.length - 1 ? 8 : 0, color: '#fff', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none' }}>
                          <span aria-hidden style={{ fontSize: '0.85rem' }}>📍</span>
                          <span>{nm}</span>
                        </a>
                      );
                    })}
                    <button type="button" onClick={() => setAddrOpen(true)}
                      style={{ marginTop: 10, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: acc, fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none' }}>
                      ⓘ View full address{locations.length > 1 ? 'es' : ''}
                    </button>
                  </div>
                ),
              }] : []),
            ].map((card, i) => (
              <div key={i} style={{ flex: 1, minWidth: 200, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: '24px 20px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: acc, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>{card.icon}</div>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, marginBottom: 6, fontSize: '0.9rem' }}>{card.title}</div>
                  <div style={{ color: 'rgba(255,255,255,0.58)', fontSize: '0.82rem', lineHeight: 1.6 }}>{card.value}</div>
                </div>
              </div>
            ))}
            {whatsappHref && (
              <div style={{ flex: 1, minWidth: 200, background: '#25d36615', border: '1px solid #25d36640', borderRadius: 14, padding: '24px 20px', display: 'flex', gap: 14, alignItems: 'center' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#25d366', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <WhatsAppIcon size={20} />
                </div>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, marginBottom: 4, fontSize: '0.9rem' }}>WhatsApp</div>
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer" style={{ color: '#25d366', fontSize: '0.82rem', textDecoration: 'none' }}>Start a chat →</a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main footer */}
        <div style={{ background: dark ? '#07070f' : '#111827', padding: '48px 24px 28px' }}>
          <div style={maxW}>
            <div className="ti-footer-cols" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 36, marginBottom: 40 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: acc, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {config.logo ? <img src={config.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: config.logoTransform }} /> : <span style={{ color: '#fff', fontWeight: 800 }}>{config.companyName?.charAt(0)}</span>}
                  </div>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>{config.companyName}</span>
                </div>
                <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.82rem', lineHeight: 1.65 }}>{config.footerTagline || config.tagline}</p>
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Quick Links</div>
                {navLinks.map(([id, label]) => (
                  <button key={id} onClick={() => smoothScroll(id)} style={{ display: 'block', background: 'none', border: 'none', color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', cursor: 'pointer', padding: '4px 0', textAlign: 'left', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = acc}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.55)'}>
                    {label}
                  </button>
                ))}
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Specialties</div>
                {conditions.slice(0, 5).map((c, i) => (
                  <div key={i} style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', padding: '3px 0' }}>{c}</div>
                ))}
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Get In Touch</div>
                {config.contact_phone && <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', padding: '3px 0' }}>📞 {config.contact_phone}</div>}
                {config.contact_email && <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', padding: '3px 0' }}>✉️ {config.contact_email}</div>}
                {whatsappHref && (
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, background: '#25d366', color: '#fff', borderRadius: 50, padding: '7px 14px', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none' }}>
                    <WhatsAppIcon size={13} /> WhatsApp Us
                  </a>
                )}
              </div>
            </div>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <p style={{ color: 'rgba(255,255,255,0.28)', fontSize: '0.78rem' }}>© {new Date().getFullYear()} {config.companyName}. All rights reserved.</p>
              <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.74rem' }}>Powered by ZoidesQ</p>
            </div>
          </div>
        </div>
        </div>
      </footer>

      {/* ── Floating WhatsApp button ── */}
      {whatsappHref && (
        <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
          style={{ position: 'fixed', bottom: 24, left: 24, zIndex: 200, width: 52, height: 52, borderRadius: '50%', background: '#25d366', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(37,211,102,0.45)', transition: 'transform 0.2s, box-shadow 0.2s', textDecoration: 'none' }}
          title="Chat on WhatsApp"
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,211,102,0.55)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(37,211,102,0.45)'; }}>
          <WhatsAppIcon size={26} />
        </a>
      )}
      {/* Dark mode toggle */}
      <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 200 }}>
        <button onClick={() => setDark(!dark)} style={{ width: 44, height: 44, borderRadius: '50%', background: dark ? '#e6edf3' : '#1a1a1a', color: dark ? '#0d1117' : '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', fontSize: '1.1rem' }}>
          {dark ? '☀️' : '🌙'}
        </button>
      </div>

      <BookAppointmentModal
        id={companyData?.company_id}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setPreselect({ doctorId: null, specId: null }); }}
        accentColor={acc}
        preselectedDoctorId={preselect.doctorId}
        preselectedSpecId={preselect.specId}
      />
      <AddressesModal open={addrOpen} onClose={() => setAddrOpen(false)} locations={locations} accent={acc} />
    </div>
  );
}
